// ============================================================================
// What is being waited on (2026-10-10): every piece of code work in flight,
// each with who holds it now and since when. The owner asked for "better
// visibility of what's being waited on": a PR whose check failed, one that
// passed with no merge card, a card nobody has answered and a build on its way
// all looked alike before (an agent "Waiting", a PR number). One model, read by
// the Now page, the chips on Prime's top bar, the Agents screen, the agents
// column, Stats, a thread's header and the Graph (`runStatus` is the one chip
// they all draw), so they never disagree. Pure; tested in waiting.test.ts.
// ============================================================================
import type { AgentRunResource } from '@/kinds/agentrun'
import type { SuggestionResource } from '@/kinds/suggestion'
import type { Builds } from '@/db/api'
import type { Tone } from './colour'
import { runsVersion, shipping } from './pipeline'

/** Who can move it next. */
export type Holder = 'you' | 'prime' | 'agent' | 'check' | 'merge' | 'build' | 'ship'

export interface Waiting {
  key: string
  holder: Holder
  title: string
  /** why it sits with the holder, in a few words */
  why: string
  /** since when it has sat there (ISO) */
  since: string
  /** where a tap goes */
  to: { run: string } | { review: string } | { card: string } | { releases: true }
  run?: AgentRunResource
  card?: SuggestionResource
}

/** In the order the owner reads them: theirs first, then what they wait for. */
export const HOLDERS: { holder: Holder; label: string }[] = [
  { holder: 'you', label: 'On you' },
  { holder: 'prime', label: 'On Prime' },
  { holder: 'agent', label: 'Agents working' },
  { holder: 'check', label: 'Checks running' },
  { holder: 'merge', label: 'Merging' },
  { holder: 'build', label: 'Building' },
  { holder: 'ship', label: 'Going to prod' },
]

/** The short word a chip shows for its holder. */
export const HOLDER_WORD: Record<Holder, string> = {
  you: 'You',
  prime: 'Prime',
  agent: 'Agent',
  check: 'Check',
  merge: 'Merge',
  build: 'Build',
  ship: 'Ship',
}

/** A session's phase in words, when nothing holds its work (an ended one, the lead). */
const PHASE_WORDS: Record<string, string> = {
  Pending: 'Waiting for a sandbox',
  Starting: 'Starting up',
  Working: 'Working',
  // a turn finished with nobody waiting on it (a merged PR, long idle): the holder says more when there is one
  Waiting: 'Idle',
  Suspended: 'Idle',
  Ended: 'Ended',
  Failed: 'Stopped',
}

/** What a session's phase means, in words. */
export const phaseWords = (phase: string) => PHASE_WORDS[phase] ?? phase

export const phaseTone = (phase: string): Tone => {
  switch (phase) {
    case 'Working':
    case 'Starting':
    case 'Pending':
      return 'running'
    case 'Failed':
      return 'warn'
  }
  return 'muted'
}

/**
 * The one chip every surface draws for an agent: who holds its work when
 * someone does (`w`, from `waitingOf`), else its phase. "Your turn" only ever
 * means the owner is the one to move it; the lead (Prime itself) is Ready or
 * Working, never anyone's turn.
 */
export const runStatus = (
  phase: string,
  w: Waiting | undefined,
  lead = false,
): { word: string; tone: Tone; holder?: Holder } => {
  if (lead) {
    if (phase === 'Working' || phase === 'Starting' || phase === 'Pending')
      return { word: 'Working', tone: 'running' }
    if (phase === 'Waiting' || phase === 'Suspended') return { word: 'Ready', tone: 'muted' }
    return { word: phaseWords(phase), tone: phaseTone(phase) }
  }
  if (!w) return { word: phaseWords(phase), tone: phaseTone(phase) }
  switch (w.holder) {
    case 'you':
      return phase === 'Failed'
        ? { word: 'Stopped', tone: 'warn', holder: 'you' }
        : { word: 'Your turn', tone: 'accent', holder: 'you' }
    case 'prime':
      return { word: 'With Prime', tone: 'muted', holder: 'prime' }
    case 'check':
      return { word: 'Checking', tone: 'running', holder: 'check' }
    case 'merge':
      return { word: 'Merging', tone: 'running', holder: 'merge' }
    default:
      return { word: phaseWords(phase), tone: 'running', holder: w.holder }
  }
}

/** In motion: nobody needs to do anything, it is running. */
export const moving = (h: Holder) =>
  h === 'agent' || h === 'check' || h === 'merge' || h === 'build' || h === 'ship'

export type CheckState = 'running' | 'passed' | 'failed' | 'timed out'

// a step about the repo's check, from either runner: agent-factory says
// "running .factory/check" and ".factory/check passed|failed[: why]|timed out
// after N minutes"; gafr says "running .factory/check from main (trusted)" and
// ".factory/check passed|failed|timedOut" (.gafr/check on a repo with a .gafr/)
const CHECK_RUNNING = /^running \.(?:factory|gafr)\/check(?:\s|$)/
const CHECK_RESULT = /^\.(?:factory|gafr)\/check\s+(.*)$/

/**
 * The runner's last word on the repo's check, from the session's events
 * (steps above; agent-factory cmd/runner, gafr internal/runner, whose check
 * result the gafr connector also writes as a step). Undefined when the session
 * never ran it.
 */
export const checkOf = (
  run: AgentRunResource,
): { state: CheckState; at: string; detail?: string } | undefined => {
  const events = run.spec.events ?? []
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]
    if (e?.kind !== 'step' || !e.detail) continue
    const d = e.detail.trim()
    const at = e.ts ?? ''
    if (CHECK_RUNNING.test(d)) return { state: 'running', at }
    const rest = CHECK_RESULT.exec(d)?.[1]?.trim()
    if (rest === undefined) continue
    if (rest === 'passed') return { state: 'passed', at }
    if (rest.startsWith('timed out') || rest === 'timedOut') return { state: 'timed out', at }
    // what failed, when the runner says ("failed: 2 tests")
    const detail = rest.replace(/^failed:?\s*/, '').trim()
    return { state: 'failed', at, ...(detail && detail !== rest ? { detail } : {}) }
  }
  return undefined
}

const repoOf = (url: string) => url.replace(/^https?:\/\/[^/]+\//, '').replace(/\.git$/, '')
const isLeadRun = (r: AgentRunResource) => r.spec.template === 'prime'
const working = (phase: string) => phase === 'Working' || phase === 'Starting' || phase === 'Pending'
const later = (...isos: (string | undefined)[]) => isos.filter(Boolean).sort().at(-1) ?? ''

/** Where a merge card stands in the merge queue (#67), when it has been tapped. */
const mergeOf = (s: SuggestionResource) =>
  (
    s.status as {
      merge?: {
        state?: string
        queued_at?: string
        started_at?: string
        updating_at?: string
        reason?: string
      }
    }
  ).merge

const prOfCard = (s: SuggestionResource) => {
  for (const op of s.spec.ops) {
    if (op.type !== 'merge_pr') continue
    const a = (op.args ?? {}) as { repo?: string; number?: number }
    if (a.repo && a.number) return { repo: a.repo, number: a.number }
  }
  return undefined
}

const minutes = (from: string, now: number) => Math.max(0, Math.round((now - Date.parse(from)) / 60000))

/** How long, said short: "4m", "2h", "3d". */
export const waited = (since: string, now: number) => {
  if (!since) return ''
  const m = minutes(since, now)
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.round(h / 24)}d`
}

/**
 * The question an agent ended on, when its last words are one: the newest line
 * of the thread is its own and closes with a question mark. Clipped for a row.
 */
export const questionOf = (run: AgentRunResource): string | undefined => {
  const msgs = run.spec.messages ?? []
  const last = msgs.at(-1)
  if (!last || last.role !== 'assistant') return undefined
  const text = last.text.replace(/\s+/g, ' ').trim()
  if (!text.endsWith('?')) return undefined
  // the sentence that asks, not the paragraph before it
  const sentences = text.split(/(?<=[.!])\s+/)
  const q = sentences.at(-1) ?? text
  return q.length > 140 ? `${q.slice(0, 139)}…` : q
}

/** An idle agent with nothing to show is not waiting on anyone after this long. */
const IDLE_DAYS = 3
/** A stopped agent is worth a word for this long. */
const FAILED_HOURS = 24

export interface WaitingInput {
  runs: AgentRunResource[]
  /** the lead's pending cards (useSessionCards) */
  cards: SuggestionResource[]
  builds?: Builds | null
  now: number
  /** each run's live phase, when the device has a newer one than the mirror */
  phaseOf?: (r: AgentRunResource) => string
}

/** One coding agent: who holds its work, or nothing when it is done or long idle. */
export const runWaiting = (
  run: AgentRunResource,
  mergeCard: SuggestionResource | undefined,
  now: number,
  phase = run.spec.status,
  leadLive = true,
): Waiting | undefined => {
  if (run.deleted_at || isLeadRun(run) || run.spec.source.provider !== 'factory') return undefined
  const title = run.spec.title || run.spec.name || 'An agent'
  const base = { key: `run:${run.id}`, title, run }
  const pr = !!run.spec.pr_number && (run.spec.pr_state ?? 'open') === 'open'
  const check = checkOf(run)
  const quiet = run.spec.last_active_at || run.spec.updated_at || ''
  if (run.spec.pr_state === 'merged' || run.spec.pr_state === 'closed') return undefined
  if (phase === 'Failed') {
    const at = run.spec.ended_at || quiet
    if (at && now - Date.parse(at) > FAILED_HOURS * 3600_000) return undefined
    return { ...base, holder: 'you', why: 'Stopped: it needs looking at', since: at, to: { run: run.id } }
  }
  if (phase === 'Ended') return undefined
  // a check runs inside the turn that publishes: the check holds it, not the agent
  if (check?.state === 'running' && working(phase))
    return {
      ...base,
      holder: 'check',
      why: `Checking, ${waited(check.at, now)} in`,
      since: check.at,
      to: { run: run.id },
    }
  if (working(phase)) {
    const step = [...(run.spec.events ?? [])].reverse().find((e) => e.kind === 'step')
    return {
      ...base,
      holder: 'agent',
      why: step?.detail ? `Working: ${step.detail}` : 'Working',
      since: later(run.spec.last_active_at),
      to: { run: run.id },
    }
  }
  const prime: 'prime' | 'you' = leadLive ? 'prime' : 'you'
  if (pr) {
    const n = run.spec.pr_number
    if (mergeCard) {
      const m = mergeOf(mergeCard)
      if (m?.state === 'merging')
        return {
          ...base,
          holder: 'merge',
          why: `Merging PR #${n}`,
          since: m.started_at || m.queued_at || '',
          to: { review: run.id },
        }
      if (m?.state === 'updating')
        return {
          ...base,
          holder: 'agent',
          why: `PR #${n} conflicts with main: its agent is merging main`,
          since: m.updating_at || m.queued_at || '',
          to: { run: run.id },
        }
      if (m?.state === 'queued')
        return {
          ...base,
          holder: 'merge',
          why: `PR #${n} queued to merge`,
          since: m.queued_at || '',
          to: { review: run.id },
        }
      if (m?.state === 'failed')
        return {
          ...base,
          holder: 'you',
          why: `Merge failed${m.reason ? `: ${m.reason}` : ''}`,
          since: mergeCard.updated_at,
          to: { card: mergeCard.id },
          card: mergeCard,
        }
      return {
        ...base,
        holder: 'you',
        why: `PR #${n} ready: merge card waiting`,
        since: mergeCard.created_at,
        to: { card: mergeCard.id },
        card: mergeCard,
      }
    }
    if (check?.state === 'failed' || check?.state === 'timed out')
      return {
        ...base,
        holder: prime,
        why: `PR #${n}: check ${check.state}, needs sending back`,
        since: check.at,
        to: { run: run.id },
      }
    if (check?.state === 'passed')
      return {
        ...base,
        holder: prime,
        why: `PR #${n}: check passed, no merge card yet`,
        since: check.at,
        to: { review: run.id },
      }
    return {
      ...base,
      holder: prime,
      why: `PR #${n} open, not reviewed`,
      since: quiet,
      to: { review: run.id },
    }
  }
  if (quiet && now - Date.parse(quiet) > IDLE_DAYS * 86400_000) return undefined
  // an agent that ended its turn on a question is waiting for an answer: the owner's
  const asked = questionOf(run)
  if (asked)
    return {
      ...base,
      holder: 'you',
      why: `Asked: ${asked}`,
      since: quiet,
      to: { run: run.id },
    }
  if (phase === 'Waiting')
    return {
      ...base,
      holder: prime,
      why: 'Finished a turn, no PR: waiting for a word',
      since: quiet,
      to: { run: run.id },
    }
  return { ...base, holder: prime, why: 'Idle, no PR yet', since: quiet, to: { run: run.id } }
}

/** Everything in flight, oldest wait first within each holder. */
export const waitingOf = ({ runs, cards, builds, now, phaseOf }: WaitingInput): Waiting[] => {
  const out: Waiting[] = []
  const leadLive = runs.some(
    (r) => isLeadRun(r) && !r.deleted_at && r.spec.status !== 'Ended' && r.spec.status !== 'Failed',
  )
  const pending = cards.filter((s) => !s.deleted_at && s.status.state === 'pending')
  const mergeCards = new Map<string, SuggestionResource>()
  for (const s of pending) {
    const pr = prOfCard(s)
    if (pr) mergeCards.set(`${pr.repo}#${pr.number}`, s)
  }
  const used = new Set<string>()
  for (const r of runs) {
    const card = r.spec.pr_number
      ? mergeCards.get(`${repoOf(r.spec.repo_url)}#${r.spec.pr_number}`)
      : undefined
    const w = runWaiting(r, card, now, phaseOf?.(r) ?? r.spec.status, leadLive)
    if (!w) continue
    if (card) used.add(card.id)
    out.push(w)
  }
  const settled = (repo: string, n: number) =>
    runs.some(
      (r) =>
        r.spec.pr_number === n &&
        repoOf(r.spec.repo_url) === repo &&
        (r.spec.pr_state === 'merged' || r.spec.pr_state === 'closed'),
    )
  let releaseCard = ''
  for (const s of pending) {
    if (used.has(s.id)) continue
    const pr = prOfCard(s)
    if (pr && settled(pr.repo, pr.number)) continue
    if (s.spec.type !== 'ask' && !s.spec.ops.length) continue
    const release = s.spec.ops.find((o) => o.type === 'release')
    if (release) releaseCard = String((release.args as { sha?: string } | undefined)?.sha ?? '')
    out.push({
      key: `card:${s.id}`,
      holder: 'you',
      title: s.spec.title,
      why: release ? 'Ship to prod?' : s.spec.type === 'ask' ? 'Prime asked you' : 'Prime suggests this',
      since: s.created_at,
      to: { card: s.id },
      card: s,
    })
  }
  if (builds) {
    for (const b of builds.builds) {
      if (b.state !== 'queued' && b.state !== 'building') continue
      out.push({
        key: `build:${b.name}`,
        holder: 'build',
        title: b.note || b.tag,
        why: b.state === 'queued' ? `Build ${b.tag} queued` : `Building ${b.tag}`,
        since: b.started,
        to: { releases: true },
      })
    }
    const rel = builds.releases[0]
    if (shipping(builds) && rel) {
      out.push({
        key: `ship:${rel.sha}`,
        holder: 'ship',
        title: `Prod to ${rel.sha.slice(0, 7)}`,
        why: 'On its way to prod',
        since: rel.at,
        to: { releases: true },
      })
    } else if (builds.outgoing && !(releaseCard && runsVersion(builds.outgoing.build.sha, releaseCard))) {
      // named by its newest change, never counted (Show less)
      const o = builds.outgoing
      out.push({
        key: `out:${o.build.sha}`,
        holder: 'you',
        title: o.commits[0]?.subject || o.build.note || o.build.tag,
        why: 'Built, not on prod yet: ship it from Releases',
        since: o.build.finished || o.build.started,
        to: { releases: true },
      })
    }
  }
  const order = (h: Holder) => HOLDERS.findIndex((x) => x.holder === h)
  return out.sort((a, b) => order(a.holder) - order(b.holder) || (a.since || '').localeCompare(b.since || ''))
}
